/**
 * The mission, in the header.
 *
 * One compact display, on every page, for as long as an assessment is
 * scheduled. It answers the only two questions worth that space: how long is
 * left, and where the work has got to. Everything else — the checks, the
 * notes, the evidence — is a page somebody opens.
 *
 * It is mounted by the shell, so moving between pages never restarts it, and
 * it is keyed by the selected organization, so changing organization clears
 * the previous one at once rather than showing another customer's assessment
 * for a moment.
 *
 * Before the assessment it is a T-minus countdown. While the assessment runs,
 * and while its determination is pending, it is a small burnt-orange rocket
 * that rises and falls a little while faint star trails pass behind it. The
 * words beside it carry the state; the rocket only shows that something is
 * under way. Once somebody marks the assessment complete, the item leaves the
 * header. The completed assessment stays on the audit preparation page.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ICON_PACK } from "../icon-pack.ts";
import { Button, IconButton, classes, inputClass } from "../primitives.tsx";
import { useDismiss } from "../use-dismiss.ts";
import { api } from "../../lib/api.ts";
import { useIllustrations } from "../space/illustrations.tsx";

type MissionPhase =
  | "not_scheduled" | "prelaunch" | "transfer" | "lunar_orbit"
  | "return" | "holding" | "complete" | "cancelled";

interface MissionView {
  readonly today: string;
  readonly plan: {
    readonly id: string;
    readonly name: string;
    readonly kind: "real" | "mock";
    readonly plannedStart: string | null;
    readonly plannedEnd: string | null;
    readonly actualEnd: string | null;
    readonly determination: string;
    readonly determinedOn: string | null;
    readonly systemName: string | null;
    readonly assessorName: string;
  } | null;
  readonly mission: {
    readonly phase: MissionPhase;
    readonly within: number;
    readonly text: string;
    readonly days: number | null;
    readonly date: string | null;
    readonly mock: boolean;
    readonly inWindow: boolean;
    readonly journey: number;
  } | null;
  readonly choices: readonly {
    readonly id: string;
    readonly name: string;
    readonly kind: "real" | "mock";
    readonly plannedStart: string | null;
    readonly inProgress: boolean;
  }[];
  readonly maySchedule: boolean;
}

/**
 * What each phase says, in plain words. `header` is the line in the header;
 * `detail` is the line in the panel.
 */
const PHASE: Record<MissionPhase, { header: string; detail: string }> = {
  not_scheduled: { header: "Start date not set", detail: "No start date is set." },
  prelaunch: { header: "", detail: "Scheduled" },
  transfer: { header: "", detail: "Starts this week" },
  lunar_orbit: { header: "Assessment underway", detail: "Assessment underway" },
  return: { header: "Determination pending", detail: "Determination pending" },
  holding: { header: "Determination pending", detail: "Determination pending. The assessment is not marked complete." },
  complete: { header: "", detail: "Complete" },
  cancelled: { header: "", detail: "Cancelled" },
};

/** The phases that show the rocket: from the start until somebody marks it complete. */
const IN_FLIGHT: ReadonlySet<MissionPhase> = new Set(["lunar_orbit", "return", "holding"]);

/** `T−06d 14h`, counted to midnight where this browser is. No seconds. */
function countdown(startDate: string, now: Date): string {
  const start = new Date(`${startDate}T00:00:00`);
  const ms = start.getTime() - now.getTime();
  const past = ms < 0;
  const total = Math.abs(ms);
  const days = Math.floor(total / 86_400_000);
  const hours = Math.floor((total % 86_400_000) / 3_600_000);
  return `T${past ? "+" : "−"}${String(days).padStart(2, "0")}d ${String(hours).padStart(2, "0")}h`;
}

/**
 * The line under the header, while the assessment window is open.
 *
 * The star field is the dark theme's way of saying that something is
 * happening. A light page cannot carry stars — it looked like dirt on the
 * screen — so it says it with a line instead: the whole width of the
 * application, two pixels of it, filled as far as the assessment has come.
 * The dark theme carries both.
 *
 * It always follows the organization's current assessment, not whichever one
 * somebody is looking at in the mission panel: the window belongs to the
 * organization, and it is the same query, so it costs no second read.
 */
export function MissionLine({ tenantId }: { readonly tenantId: string | null }) {
  const mission = useQuery({
    queryKey: ["audit-mission", tenantId, null],
    queryFn: () => api.get<MissionView>("/api/audit/mission"),
    enabled: tenantId !== null,
    staleTime: 60_000,
  });
  const state = mission.data?.mission ?? null;
  if (state === null || !state.inWindow) return null;
  const width = `${Math.round(Math.min(1, Math.max(0, state.journey)) * 100)}%`;
  const fill = (
    <div
      className="h-full bg-gradient-to-r from-accent to-highlight transition-[width] duration-700"
      style={{ width }}
    />
  );
  return (
    <div data-testid="mission-line" className="w-full">
      {/*
        On a phone this line is the only thing that says an assessment is
        running, because the mission display does not fit a phone's bar — so
        it carries the words for a reader who does not see it.
      */}
      <div
        role="img"
        aria-label={`Mission: ${state.text}`}
        className="h-0.5 w-full bg-grid tablet:hidden"
      >
        {fill}
      </div>
      {/*
        On a wide screen the mission display says all of this in words beside
        it, and a screen reader should hear it once.
      */}
      <div aria-hidden="true" className="hidden h-0.5 w-full bg-grid tablet:block">
        {fill}
      </div>
    </div>
  );
}

export function MissionDisplay({
  tenantId,
  phone = false,
}: {
  readonly tenantId: string | null;
  /**
   * The phone's version: a full-width row under the bar, shown only while
   * the assessment window is open. The phone's bar has no room for it
   * otherwise, and outside the window the line under the header is enough.
   */
  readonly phone?: boolean;
}) {
  const [planId, setPlanId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const panel = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(panel, open, close);

  // The organization is part of the key, so changing it drops the previous
  // organization's assessment at once rather than showing it while a new read
  // is in flight.
  const mission = useQuery({
    queryKey: ["audit-mission", tenantId, planId],
    queryFn: () =>
      api.get<MissionView>(`/api/audit/mission${planId === null ? "" : `?planId=${planId}`}`),
    enabled: tenantId !== null,
    staleTime: 60_000,
  });

  // The hour hand only. A countdown that re-renders every second says nothing
  // new and would announce itself to a screen reader every second.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => { setPlanId(null); }, [tenantId]);

  // The star field belongs to an organization with an assessment in front of
  // it: from thirty days before the start until somebody marks it complete.
  // The attribute is written here because this is where the window is already
  // known, and it is removed when this display goes away.
  const inWindow = mission.data?.mission?.inWindow ?? false;
  useEffect(() => {
    // One display owns the attribute: the wide header's, which is always mounted.
    if (phone) return;
    const root = document.documentElement;
    if (inWindow) root.setAttribute("data-sky", "on");
    else root.removeAttribute("data-sky");
    return () => root.removeAttribute("data-sky");
  }, [inWindow, phone]);

  const view = mission.data;
  if (view === undefined) return null;

  if (phone && !(view.mission?.inWindow ?? false)) return null;
  // A completed assessment leaves the header; the header is as it is with
  // nothing scheduled. The record stays on the audit preparation page.
  if (view.plan === null || view.mission === null || view.mission.phase === "cancelled" || view.mission.phase === "complete") {
    if (!view.maySchedule || phone) return null;
    return <ScheduleAction />;
  }

  const phase = view.mission.phase;
  const flying = IN_FLIGHT.has(phase);
  const counting = phase === "prelaunch" || phase === "transfer";
  const headline = counting
    ? view.plan.plannedStart === null ? PHASE.not_scheduled.header : countdown(view.plan.plannedStart, now)
    : PHASE[phase].header;
  const kind = view.plan.kind === "mock" ? "Mock assessment" : "Assessment";

  const detail = (
    <MissionDetail
      view={view}
      clock={counting && view.plan.plannedStart !== null ? countdown(view.plan.plannedStart, now) : ""}
      onChoose={(id) => setPlanId(id)}
      onClose={close}
    />
  );

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        data-testid={phone ? "mission-display-phone" : "mission-display"}
        data-phase={phase}
        className={phone
          ? "flex h-12 w-full items-center gap-3 border-t border-divider bg-paper px-4 text-left hover:bg-selected"
          : classes(
            "flex h-11 max-w-[60vw] items-center gap-3 rounded-arc border border-divider bg-paper px-3 text-left hover:bg-selected sm:h-12",
            flying ? "w-[17.5rem]" : "w-[11rem]",
          )}
        // The accessible name carries the state in words. It is not a live
        // region: nobody needs the countdown read out every hour. It avoids
        // the word "start", so it never answers to a search for a Start button.
        aria-label={`${kind} ${counting ? `countdown, ${headline}` : `, ${headline}`}. Open the assessment details.`.replace(" ,", ",")}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs text-muted">{kind}</span>
          <span className="block truncate text-sm font-semibold tabular-nums">{headline}</span>
        </span>
        {flying && <RocketFlight compact={phone} />}
      </button>

      {open && !phone && (
        <>
          <div className="fixed inset-0 z-30 bg-ink/30 sm:hidden" aria-hidden="true" />
          <div
            ref={panel}
            role="dialog"
            aria-label="Assessment details"
            className={classes(
              "fixed inset-x-0 bottom-0 z-40 flex max-h-[85vh] flex-col gap-3 overflow-y-auto rounded-t-arc border-t border-divider bg-paper p-4 shadow-xl",
              "sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-1 sm:max-h-none sm:w-[26rem] sm:overflow-visible sm:rounded-arc sm:border sm:p-4",
            )}
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            {detail}
          </div>
        </>
      )}
      {/* On a phone the sheet goes to the page body. The phone's bar is its
          own stacking layer, and a sheet inside it would sit under the tabs. */}
      {open && phone && createPortal(
        <>
          <div className="fixed inset-0 z-50 bg-ink/30" aria-hidden="true" />
          <div
            ref={panel}
            role="dialog"
            aria-label="Assessment details"
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col gap-3 overflow-y-auto rounded-t-arc border-t border-divider bg-paper p-4 shadow-xl"
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            {detail}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

/** Nothing is scheduled, and this person may schedule one. */
function ScheduleAction() {
  const navigate = useNavigate();
  return (
    <Button
      size="small"
      variant="quiet"
      onClick={() => void navigate({ to: "/audit-prep" })}
    >
      Schedule assessment
    </Button>
  );
}

/** Whole days from one ISO day to another. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? "" : "s"}`;

/** The one line of timing under the phase, from the saved dates. */
function timingOf(view: MissionView, clock: string): string {
  const plan = view.plan!;
  const mission = view.mission!;
  switch (mission.phase) {
    case "prelaunch":
    case "transfer":
      return plan.plannedStart === null ? "" : `Starts ${plan.plannedStart} · ${clock}`;
    case "lunar_orbit": {
      if (plan.plannedStart === null || plan.plannedStart > view.today) return "";
      const day = daysBetween(plan.plannedStart, view.today) + 1;
      const length = plan.plannedEnd === null ? null : daysBetween(plan.plannedStart, plan.plannedEnd) + 1;
      if (length !== null && day > length) return `Day ${day}. The planned end was ${plan.plannedEnd}.`;
      return length === null ? `Day ${day}` : `Day ${day} of ${length}`;
    }
    case "return":
      return mission.date === null ? "" : `Determination expected by ${mission.date}`;
    case "holding": {
      const end = plan.plannedEnd ?? mission.date;
      return end === null ? "" : `Planned end ${end}, ${plural(daysBetween(end, view.today), "day")} ago`;
    }
    default:
      return "";
  }
}

function MissionDetail({
  view,
  clock,
  onChoose,
  onClose,
}: {
  readonly view: MissionView;
  readonly clock: string;
  readonly onChoose: (id: string) => void;
  readonly onClose: () => void;
}) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const plan = view.plan!;
  const mission = view.mission!;
  const timing = timingOf(view, clock);

  const complete = useMutation({
    mutationFn: () => api.post(`/api/audit/plans/${plan.id}/complete`, {}),
    onSuccess: async () => {
      onClose();
      await client.invalidateQueries({ queryKey: ["audit-mission"] });
      await client.invalidateQueries({ queryKey: ["audit-prep"] });
    },
  });

  const subtitle = [plan.kind === "mock" ? "Mock assessment" : "Formal assessment", plan.systemName, plan.assessorName]
    .filter((part) => part !== null && part !== "")
    .join(" · ");

  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{plan.name}</p>
          <p className="text-sm text-muted">{subtitle}</p>
        </div>
        <span className="-mr-1 -mt-1">
          <IconButton icon="close" label="Close" onClick={onClose} />
        </span>
      </div>

      <div data-testid="mission-phase">
        <p className="font-semibold">{PHASE[mission.phase].detail}</p>
        {timing !== "" && <p className="text-sm tabular-nums text-muted">{timing}</p>}
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm min-[420px]:grid-cols-3">
        <div>
          <dt className="text-muted">Start</dt>
          <dd className="tabular-nums">{plan.plannedStart ?? "Not set"}</dd>
        </div>
        <div>
          <dt className="text-muted">Planned end</dt>
          <dd className="tabular-nums">{plan.plannedEnd ?? "Not set"}</dd>
        </div>
      </dl>

      <dl className="text-sm" data-testid="mission-determination">
        <dt className="text-muted">Determination</dt>
        <dd>
          {plan.determination === ""
            ? "Not recorded yet"
            : `${plan.determination.replace(/_/g, " ")}${plan.determinedOn === null ? "" : `, recorded ${plan.determinedOn}`}`}
        </dd>
      </dl>

      {plan.actualEnd === null && IN_FLIGHT.has(mission.phase) && (
        <Button
          size="small"
          variant="primary"
          disabled={complete.isPending}
          onClick={() => complete.mutate()}
        >
          Mark the assessment complete
        </Button>
      )}

      {view.choices.length > 1 && (
        <label className="text-sm">
          <span className="block text-muted">Which assessment</span>
          <select
            className={classes(inputClass, "mt-1 py-1")}
            value={plan.id}
            onChange={(event) => onChoose(event.target.value)}
          >
            {view.choices.map((choice) => (
              <option key={choice.id} value={choice.id}>
                {`${choice.kind === "mock" ? "Mock" : "Formal"}: ${choice.name}`
                  + (choice.plannedStart === null ? "" : ` (${choice.plannedStart})`)
                  + (choice.inProgress ? ", running" : "")}
              </option>
            ))}
          </select>
        </label>
      )}

      <Button
        size="small"
        onClick={() => { onClose(); void navigate({ to: "/audit-prep" }); }}
      >
        Open audit preparation
      </Button>
    </>
  );
}

// ---------------------------------------------------------------------------
// The rocket
// ---------------------------------------------------------------------------

/**
 * True while the page is in front. The header is always on screen while the
 * page is, so the page's own visibility is the one that matters.
 */
function usePageShown(): boolean {
  const [shown, setShown] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const onChange = () => setShown(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return shown;
}

/**
 * The star trails: three layers, far to near. A nearer layer moves faster,
 * is a little brighter, and has longer streaks, which reads as depth. Each
 * layer is twice the box wide and repeats itself, so the loop has no seam.
 * Positions are in percent of one half, then repeated in the second half.
 */
const TRAIL_LAYERS: readonly { seconds: number; opacity: number; length: number; streaks: readonly [number, number][] }[] = [
  { seconds: 11, opacity: 0.35, length: 5, streaks: [[8, 22], [46, 74], [78, 40]] },
  { seconds: 7.5, opacity: 0.5, length: 7, streaks: [[20, 64], [62, 30]] },
  { seconds: 5, opacity: 0.6, length: 10, streaks: [[34, 84], [88, 14]] },
];

/**
 * A small burnt-orange rocket, flying right, in its own box.
 *
 * The motion is CSS: a slow rise and fall, and the trails passing behind it
 * to the left. There are no timers. The box pauses while the page is hidden. With reduced motion, or with motion turned off
 * in the presentation settings, the rocket is still and there are no trails.
 */
function RocketFlight({ compact = false }: { readonly compact?: boolean }) {
  const { motion } = useIllustrations();
  const shown = usePageShown();
  const rocket = ICON_PACK.rocket;
  const path = Array.isArray(rocket.path) ? rocket.path[1] : rocket.path;
  return (
    <span
      aria-hidden="true"
      data-testid="mission-rocket"
      data-flight={!motion ? "still" : shown ? "moving" : "paused"}
      className={classes("relative flex shrink-0 items-center justify-center overflow-hidden rounded-arc", compact ? "h-8 w-12" : "h-9 w-16")}
    >
      {motion && TRAIL_LAYERS.map((layer) => (
        <span key={layer.seconds} className="arc-flight-trails" style={{ animationDuration: `${layer.seconds}s`, opacity: layer.opacity }}>
          {[0, 50].flatMap((half) => layer.streaks.map(([x, y]) => (
            <span
              key={`${half}-${x}-${y}`}
              className="arc-flight-streak"
              style={{ left: `${half + x / 2}%`, top: `${y}%`, width: `${layer.length}px` }}
            />
          )))}
        </span>
      ))}
      <span className={classes("relative text-highlight", motion && "arc-flight-bob")}>
        {/* The artwork points up and to the right; turned 45° it flies right. */}
        <svg viewBox={`0 0 ${rocket.width} ${rocket.height}`} className={compact ? "h-4 w-4" : "h-[1.125rem] w-[1.125rem]"} style={{ transform: "rotate(45deg)" }} fill="currentColor">
          <path d={path} />
        </svg>
      </span>
    </span>
  );
}
